import { useEffect, useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory, useLocation } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Chapter, type ChapterLead } from "../components/Chapter";
import { TrendChart } from "../components/Chart";
import { Hero } from "../components/Hero";
import { Insight } from "../components/Insight";
import { LogWeightSheet } from "../components/LogWeightSheet";
import { Range } from "../components/Range";
import { RowGroup } from "../components/Rows";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { waistToHeight, whtrAimCm } from "../domain/body";
import { cycleAffected, cycleInfo, cycleOn, hormonal, phaseOf, prePeriodDay } from "../domain/cycle";
import { cycleInsights } from "../domain/cycleView";
import { addDays, dayLabel, dayMonth, daysBetween, parseDay, todayStr } from "../domain/date";
import { chairStandLine, testSeries } from "../domain/fitnessTests";
import { programmeOf } from "../domain/readiness";
import { dayMark } from "../domain/dayMarks";
import { foodInsights, nutrientAverages, proteinDays, type NutrientKey } from "../domain/foodTrends";
import type { Insight as InsightData } from "../domain/insight";
import { foodOn } from "../domain/intake";
import { dayIn, dayLedger } from "../domain/ledger";
import { movementInsights, stepsWindow, vsPrevious } from "../domain/movement";
import { phase, weekNo } from "../domain/plan";
import { level } from "../domain/progress";
import {
  bodyInsights,
  chartWindow,
  energyInsights,
  energyView,
  heroInsights,
  heroView,
  hoursMinutes,
  readingsTagged,
  signed,
  trendLabel,
  trendLine,
  trendUnit,
  trendView,
} from "../domain/progressView";
import { nextBadge } from "../domain/stats";
import { activeInWindow, consistency, lifetime, trainingInsights, whoMinutes } from "../domain/training";
import { ETA_MIN_SPAN_DAYS, waistSeries, weightSeries } from "../domain/trend";
import type { KayaState } from "../domain/types";
import { useDeviceAnswers, useKaya } from "../store/kayaStore";
import "./Progress.css";

/** Plan days before Training's sentences mean anything (training.ts's own threshold). */
const PLAN_MIN_DAYS = 7;
/** Logged food days before Food and Energy are more than a count (the builders' threshold). */
const FOOD_MIN_DAYS = 7;

const PHASE_WORD = { menstrual: "period", follicular: "follicular", ovulation: "ovulation", luteal: "luteal" } as const;

function pick(list: InsightData[], ...keys: string[]): InsightData[] {
  return keys.map((k) => list.find((i) => i.key === k)).filter((i): i is InsightData => !!i);
}

function plural(n: number, one: string, many = one + "s"): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** A chapter the data does not yet support, folded into "Still gathering" (ProgressFirstWeek). */
interface Gathering {
  name: string;
  insight: InsightData;
}

/** An Insight row whose sentence opens with the chapter's name in bold (kit §23, first-week board). */
function GatherRow({ g, first }: { g: Gathering; first: boolean }) {
  const { insight } = g;
  return (
    <div className={`k-insight${first ? " k-insight--first" : ""}${insight.wait ? " k-insight--wait" : ""}`}>
      <p className="k-insight__text">
        <strong className="pg-gather__name">{g.name}</strong> · {insight.text}
      </p>
      <p className="k-insight__evidence">
        <span>{insight.basedOn}</span>
        {insight.wait ? <Tag tone="planned">{insight.wait}</Tag> : null}
      </p>
    </div>
  );
}

/** One weigh-in per day at most, as the reading logged that day. */
function weightMap(S: KayaState): (ds: string) => number | null {
  const m = new Map(weightSeries(S).map((p) => [p.date, p.v]));
  return (ds) => m.get(ds) ?? null;
}

/**
 * Progress — one body profile read top to bottom (ADR-033): the hero answers "is it working?"
 * with the logged weight, its trend and the goal date as a range; then one chapter per focused
 * page, each a lead figure or two and the sentence that joins it to the rest. A chapter with
 * nothing to say yet folds into "Still gathering" rather than showing a row of dashes.
 *
 * Body and the hero are as of today. Energy, Food and Movement end yesterday: today's half-logged
 * food and half-walked steps would drag a 28-day average.
 */
export default function Progress() {
  const S = useKaya((s) => s.S);
  const device = useDeviceAnswers();
  const history = useHistory();
  const location = useLocation();
  const [logOpen, setLogOpen] = useState(false);

  // The PWA shortcut promises "Log weight": /progress?log=1 opens the sheet, then drops the
  // query so Back or a re-render does not open it again.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("log") === "1") {
      setLogOpen(true);
      history.replace(location.pathname);
    }
  }, [location.search, location.pathname, history]);

  const today = todayStr();
  // Seven chapters' builders walk up to a year of logs (~80 ms on a laptop for a year of data);
  // without this they all ran again whenever the weigh-in sheet opened or closed.
  const {
    tv,
    hv,
    heroTagged,
    showRange,
    heroRows,
    showChart,
    gathering,
    lastWaist,
    bodyLeads,
    bodyIns,
    food,
    foodThin,
    energyLeads,
    agree,
    foodLeads,
    fIns,
    young,
    trainingLeads,
    trainingIns,
    moveLeads,
    mIns,
    cycleLeads,
    cIns,
    lv,
    levelIns,
  } = useMemo(() => {
    const yesterday = addDays(today, -1);
    const cyc = cycleOn(S);
    const affected = (ds: string) => cycleAffected(S, ds);
    const weights = weightMap(S);

    /* ---------- hero ---------- */
    const tv = trendView(S, "weight", today, cyc ? affected : undefined);
    const hv = heroView(S, today, affected);
    const hIns = heroInsights(S, today, affected);
    // Cycle-explained weigh-ins keep their colour on the hero; the shaded days stay on Body.
    const heroTagged = cyc ? new Set(readingsTagged(S, affected, today).readings.map((r) => r.date)) : undefined;
    const etaIns = hIns.find((i) => i.key === "hero.eta");
    // The Range carries the goal date; the eta sentence shows only while there is no date to draw.
    const showRange = hv.target !== null && hv.weighIns > 0 && !etaIns?.wait;
    const heroRows = hIns.filter((i) => i.key !== "hero.eta" || !hv.eta);
    const showChart = tv.raw.length >= 2 && hv.daysSoFar >= ETA_MIN_SPAN_DAYS;

    const gathering: Gathering[] = [];

    /* ---------- body ---------- */
    const waists = waistSeries(S).filter((p) => p.date <= today);
    const lastWaist = waists[waists.length - 1];
    const firstWaist = waists[0];
    const bodyIns = pick(bodyInsights(S, today, affected), "body.composition", "body.waist", "body.cycle");
    const whtr = lastWaist ? waistToHeight(S, lastWaist.v) : null;
    const aim = whtrAimCm(S);
    const bodyLeads: ChapterLead[] = lastWaist
      ? [
          {
            figure: lastWaist.v.toFixed(1),
            unit: "cm",
            label: `Waist · ${dayLabel(lastWaist.date)}`,
            note:
              waists.length > 1
                ? `${signed(lastWaist.v - firstWaist.v, 1)} cm since ${dayMonth(firstWaist.date)}`
                : undefined,
          },
          {
            figure: whtr === null ? null : whtr.toFixed(2),
            label: "Waist to height",
            note: aim === null ? undefined : `under 0.50 · ${Math.floor(aim)} cm`,
          },
        ]
      : [];
    if (!lastWaist) {
      const w = bodyIns.find((i) => i.key === "body.waist");
      if (w) gathering.push({ name: "Body", insight: w });
    }

    /* ---------- energy and food ---------- */
    const food = foodOn(S);
    const ev = energyView(S, today, 28, affected); // its window already ends yesterday
    const eIns = energyInsights(S, today, 28, affected);
    const agree = pick(eIns, "energy.agree");
    const foodThin = food && ev.paired < FOOD_MIN_DAYS;
    if (foodThin && agree[0]) gathering.push({ name: "Food and Energy", insight: agree[0] });

    const energyLeads: ChapterLead[] = food
      ? [
          {
            figure: ev.deficit === null ? null : signed(Math.round(ev.deficit / 10) * 10),
            label: "In less Out · a day",
            note: (
              <>
                <Tag tone="est" /> {ev.of} days
              </>
            ),
          },
          { figure: ev.logged, unit: `of ${ev.of}`, label: "Days with food logged" },
        ]
      : [
          {
            figure: ev.workAvg === null ? null : `~${Math.round(ev.workAvg).toLocaleString()}`,
            unit: "kcal",
            label: "Work · a day",
            note: (
              <>
                <Tag tone="est" /> {ev.of} days
              </>
            ),
          },
        ];

    const fDeps = {
      budget: (ds: string) => dayLedger(S, ds, parseDay(today), device).budget,
      prePeriod: (ds: string) => prePeriodDay(S, ds),
      cycleOn: cyc,
    };
    const fIns = food ? pick(foodInsights(S, yesterday, 28, fDeps), "food.protein") : [];
    const na = food ? nutrientAverages(S, yesterday, 28) : null;
    const pd = food ? proteinDays(S, yesterday, 28) : null;
    const weakest = na
      ? (["fb", "fe", "ca", "pot"] as NutrientKey[])
          .map((k) => na.by[k])
          .filter((r) => r.known >= FOOD_MIN_DAYS && r.avg !== null && r.target !== null && r.target > 0)
          .sort((a, b) => (a.avg ?? 0) / (a.target ?? 1) - (b.avg ?? 0) / (b.target ?? 1))[0]
      : undefined;
    const protein = na ? na.by.p.avg : null;
    const foodLeads: ChapterLead[] = [
      {
        figure: protein === null ? null : Math.round(protein).toLocaleString(),
        unit: "g",
        label: "Protein a day",
        note: pd ? `of ${pd.target} g` : undefined,
      },
      weakest
        ? {
            figure: Math.round(weakest.avg ?? 0).toLocaleString(),
            unit: weakest.unit,
            label: `${weakest.label} · weakest nutrient`,
            note: `of ${Math.round(weakest.target ?? 0).toLocaleString()} ${weakest.unit}`,
          }
        : { figure: null, label: "Weakest nutrient" },
    ];

    /* ---------- training ---------- */
    const life = lifetime(S, today);
    const planDays = daysBetween(life.since, today) + 1;
    const young = planDays < PLAN_MIN_DAYS;
    const who = whoMinutes(S, today);
    const weekBefore = activeInWindow(S, addDays(today, -7), 7);
    const cons = consistency(S, today);
    const tIns = trainingInsights(S, today);
    const trainingLeads: ChapterLead[] = young
      ? [
          {
            figure: hoursMinutes(who.minutes),
            label: `Active · this week · ${plural(life.sessions, "session")}`,
          },
        ]
      : [
          { figure: hoursMinutes(who.minutes), label: "Active · last 7 days", note: `${hoursMinutes(weekBefore)} week before` },
          {
            figure: cons.of ? `${cons.pct} %` : null,
            label: "Consistent · 28 days",
            note: `${cons.n} of ${plural(cons.of, "session")}`,
          },
        ];
    // A Steady person's chapter leads with the chair stand, minutes second, kcal never (ADR-054)
    const chair = programmeOf(S) === "steady" ? testSeries(S, "chair") : [];
    const lastChair = chair.length ? chair[chair.length - 1] : null;
    if (lastChair) {
      const line = chairStandLine(S);
      const minutes = trainingLeads[0];
      trainingLeads.length = 0;
      trainingLeads.push(
        {
          figure: lastChair.value,
          label: `Chair stands · ${dayMonth(lastChair.ds)}`,
          note: line === null ? undefined : `the range starts at ${line}`,
        },
        minutes
      );
    }
    const trainingIns = lastChair ? pick(tIns, "training.tests") : pick(tIns, young ? "training.consistency" : "training.who");

    /* ---------- movement ---------- */
    const sw = stepsWindow(S, yesterday, 28);
    const vp = vsPrevious(S, yesterday, 28);
    const moveThin = sw.measuredDays < 7;
    const mIns = pick(
      movementInsights(S, yesterday, 28, { prePeriod: fDeps.prePeriod, cycleOn: cyc, weights }),
      "move.weekend"
    );
    const moveLeads: ChapterLead[] = [
      {
        figure: sw.avg === null ? null : (Math.round(sw.avg / 10) * 10).toLocaleString(),
        label: moveThin ? `Steps a day · ${sw.measuredDays} days measured` : "Steps a day · 28 days",
        note: (
          <>
            {moveThin && sw.phoneDays > 0 ? <Tag tone="phone" /> : null} of {sw.goal.toLocaleString()}
          </>
        ),
      },
    ];
    if (vp) moveLeads.push({ figure: signed(Math.round(vp.delta / 10) * 10), label: "a day on the 28 before" });

    /* ---------- cycle ---------- */
    const ci = cyc ? cycleInfo(S, today) : null;
    const cIns = cyc
      ? cycleInsights(S, today, {
          weights,
          // complete days only: a marked day is not what was eaten that day (ADR-049)
          intake: (ds) => (dayMark(S, ds) ? null : dayIn(S, ds)),
          deficit: ev.deficit,
          activeMin: who.minutes,
        })
      : [];
    const cycLen = S.cycle.length || 28;
    const ph = cyc ? phaseOf(S, today) : null;
    const horm = cyc && hormonal(S);
    const cycleLeads: ChapterLead[] =
      ci && ci.day !== null
        ? [
            { figure: `Day ${ci.day}`, label: `of ~${cycLen}${ph ? ` · ${PHASE_WORD[ph]}` : ""}` },
            {
              figure: ci.next ? `~${dayLabel(ci.next)}` : null,
              label: horm ? "Next bleed" : "Next period",
              note: <Tag tone="est" />,
            },
          ]
        : [];
    if (cyc && !cycleLeads.length) {
      const now = cIns.find((i) => i.key === "cycle.now");
      if (now) gathering.push({ name: "Cycle", insight: now });
    }

    /* ---------- level ---------- */
    const lv = level(S);
    const nb = nextBadge(S);
    const sinceWord = dayMonth(life.since);
    const foodDays = Object.keys(S.logs).filter((ds) => ds <= today && dayIn(S, ds) !== null).length;
    const levelIns: InsightData = young
      ? {
          key: "level.next",
          text: "Every logged day adds XP.",
          basedOn: [
            plural(life.sessions, "session"),
            plural(hv.weighIns, "weigh-in"),
            ...(food ? [plural(foodDays, "food day")] : []),
          ]
            .join(", ")
            .replace(/, ([^,]*)$/, " and $1"),
        }
      : {
          key: "level.next",
          text:
            (nb ? `Next badge: ${nb.b.n}, ${Math.max(1, Math.ceil(nb.n - nb.a)).toLocaleString()} more to go. ` : "") +
            `Since ${sinceWord}: ${plural(life.sessions, "session")}, ${hoursMinutes(life.minutes)} active, ${life.km} km.`,
          basedOn: `every session and walk since ${sinceWord}`,
        };

    return {
      tv,
      hv,
      heroTagged,
      showRange,
      heroRows,
      showChart,
      gathering,
      lastWaist,
      bodyLeads,
      bodyIns,
      food,
      foodThin,
      energyLeads,
      agree,
      foodLeads,
      fIns,
      young,
      trainingLeads,
      trainingIns,
      moveLeads,
      mIns,
      cycleLeads,
      cIns,
      lv,
      levelIns,
    };
  }, [S, today]);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar
            title="Progress"
            sub={`Since ${dayLabel(S.profile.startDate)} · week ${weekNo(S)}, phase ${phase(S) + 1}`}
          />
          <div className="pg">
            <Hero
              label={trendLabel(tv, today)}
              figure={tv.now === null ? null : tv.now.toFixed(1)}
              unit={trendUnit(tv)}
              line={trendLine(tv)}
              sub={tv.raw.length ? undefined : "Log one and the trend starts from it."}
              visual={
                showChart || showRange || heroRows.length ? (
                  <div className="pg-herorows">
                    {showChart ? (
                      <TrendChart
                        pts={chartWindow(tv.raw, today)}
                        trend={chartWindow(tv.trend, today)}
                        projection={tv.projection ? chartWindow(tv.projection, today) : undefined}
                        target={tv.target ?? undefined}
                        unit={tv.unit}
                        readings="weigh-ins"
                        tagged={heroTagged}
                      />
                    ) : null}
                    {showRange && hv.target !== null ? (
                      <Range
                        label={
                          hv.eta
                            ? `Goal date · ${hv.target} kg · likely ${dayMonth(hv.eta.from)} – ${dayMonth(hv.eta.to)}`
                            : `Goal date · ${hv.target} kg`
                        }
                        likely={hv.eta ? hv.eta.likely : null}
                        from={hv.eta?.from}
                        to={hv.eta?.to}
                        detail="An estimate, not a promise."
                        ariaLabel={
                          hv.eta
                            ? `Goal date around ${dayLabel(hv.eta.likely)}, likely between ${dayMonth(hv.eta.from)} and ${dayMonth(hv.eta.to)}`
                            : "No goal date yet"
                        }
                      />
                    ) : null}
                    {heroRows.map((i, n) => (
                      <Insight key={i.key} insight={i} first={n === 0 && !showChart && !showRange} />
                    ))}
                  </div>
                ) : undefined
              }
            />

            {lastWaist ? (
              <Chapter title="Body" to="/progress/body" linkLabel="Body in full" leads={bodyLeads} insights={pick(bodyIns, "body.composition", "body.cycle")} />
            ) : null}

            {!foodThin ? (
              <Chapter title="Energy" to="/progress/energy" linkLabel="Energy in full" leads={energyLeads} insights={agree} />
            ) : null}

            {food && !foodThin ? (
              <Chapter title="Food" to="/progress/food" linkLabel="Food in full" leads={foodLeads} insights={fIns} />
            ) : null}

            <Chapter title="Training" to="/progress/training" linkLabel="Training in full" leads={trainingLeads} insights={trainingIns} />

            <Chapter title="Movement" to="/progress/movement" linkLabel="Movement in full" leads={moveLeads} insights={mIns} />

            {cycleLeads.length ? (
              <Chapter title="Cycle" to="/progress/cycle" linkLabel="Cycle in full" leads={cycleLeads} insights={pick(cIns, "cycle.next")} />
            ) : null}

            {gathering.length ? (
              <RowGroup heading="Still gathering">
                {gathering.map((g, n) => (
                  <GatherRow key={g.name} g={g} first={n === 0} />
                ))}
              </RowGroup>
            ) : null}

            <Chapter
              title="Level"
              to="/progress/level"
              linkLabel="Level in full"
              leads={[
                {
                  figure: `Level ${lv.n}`,
                  label: young ? `${lv.name} · ${lv.xp.toLocaleString()} XP` : lv.name,
                  note:
                    young || lv.to === null ? undefined : `${(lv.to - lv.xp).toLocaleString()} XP to Level ${lv.n + 1}`,
                },
              ]}
              insights={[levelIns]}
            />
          </div>

          <ActionBar primary={{ label: "Log weight", icon: I.plus, onClick: () => setLogOpen(true) }} />
        </Screen>
        <LogWeightSheet open={logOpen} onClose={() => setLogOpen(false)} />
      </IonContent>
    </IonPage>
  );
}
